<template>
  <el-row class="info-charts">
    <card-chart
      v-loading="loading.FEED || getAllGraphLoading"
      :columns="getScheduleFeedColumns"
      :tableData="getFeedTableData"
      :chartConfig="getScheduleFeedChartOptions"
      :xAxisTitle="
        getSelectedPonds.length > 1 ? $t('Comn_doc_days') : $t('Comn_date')
      "
      :yAxisTitle="$t('Comn_feed_kg')"
      :xAxisType="getScheduleFeedChartOptions.xAxis.type"
      :initDateRangeArr="getDateRangeForFeedGraph"
      :datePickerOptions="getDatePickerOptions"
      keyToStoreDefaults="feed-graph-table-pref"
      datePickerFormat="yyyy-MM-dd"
      :datePickerDisabled="selectedPonds.length > 1"
      @date-query="handleDateQuery($event, 'FEED')"
    >
      <p slot="title">{{ $t("Comn_feed") }}</p>
      <el-row slot="legend" class="legend-container">
        <el-col class="legend" :span="14">
          <span
            v-for="item in getLegend"
            :key="item.color"
            class="legend__item"
          >
            <div
              class="color-box"
              :style="{ 'background-color': item.color }"
            />
            <p class="title">{{ item.name }}</p>
          </span>
        </el-col>
        <el-col :span="9" class="legend legend--right">
          <span class="legend__item legend__item--solid-line">
            <hr class="despFeed" />
            <p class="title">{{ $t("PM_dispensed_feed") }}</p>
          </span>
          <span class="legend__item legend__item--dash-line">
            <hr />
            <p class="title">{{ $t("PM_day_feed") }}</p>
          </span>
        </el-col>
      </el-row>
    </card-chart>
    <card-chart
      v-loading="loading.DO || getAllGraphLoading"
      :columns="getDoColumns"
      :tableData="getDoTableData"
      :chartConfig="getDOChartOptions"
      :xAxisTitle="$t('Comn_date_and_time')"
      :yAxisTitle="[$t('PM_dispensed_feed_kg'), $t('Comn_do_with_units')]"
      :initDateRangeArr="getDateRangeForDOGraph"
      :datePickerOptions="getHourlyDatePickerOptions"
      keyToStoreDefaults="dissolved-oxygen-graph-table-pref"
      datePickerFormat="yyyy-MM-dd"
      :datePickerDisabled="false"
      @date-query="handleDateQuery($event, 'DO')"
    >
      <p slot="title">{{ "DO" + " vs " + $t("Comn_hourly_feed") }}</p>
      <el-row slot="legend" class="legend-container">
        <el-col class="legend" :span="15">
          <span
            v-for="item in getLegend"
            :key="item.color"
            class="legend__item"
          >
            <div
              class="color-box"
              :style="{ 'background-color': item.color }"
            />
            <p class="title">{{ item.name }}</p>
          </span>
        </el-col>
        <el-col :span="9" class="legend legend--right">
          <span class="legend__item legend__item--dash-line">
            <hr />
            <p class="title">DO</p>
          </span>
        </el-col>
      </el-row>
    </card-chart>
    <card-chart
      v-loading="loading.ABW || getAllGraphLoading"
      :columns="getAbwColumns"
      :tableData="getAbwTableData"
      :chartConfig="getABWChartOptions"
      :xAxisTitle="$t('Comn_date')"
      :yAxisTitle="[$t('abw_gms'), `${$t('Comn_biomass')} (kgs)`]"
      :initDateRangeArr="getDateRangeForABWGraph"
      :datePickerOptions="getDatePickerOptions"
      keyToStoreDefaults="abw-graph-table-pref"
      datePickerFormat="yyyy-MM-dd"
      :datePickerDisabled="false"
      @date-query="handleDateQuery($event, 'ABW')"
    >
      <p slot="title">{{ $t("ABW_abw") + " vs " + $t("Comn_biomass") }}</p>
      <el-row slot="legend" class="legend-container">
        <el-col class="legend" :span="15">
          <span
            v-for="item in getLegend"
            :key="item.color"
            class="legend__item"
          >
            <div
              class="color-box"
              :style="{ 'background-color': item.color }"
            />
            <p class="title">{{ item.name }}</p>
          </span>
        </el-col>
        <el-col :span="9" class="legend legend--right">
          <span class="legend__item legend__item--dash-line">
            <hr />
            <p class="title">{{ $t("Comn_biomass") }}</p>
          </span>
        </el-col>
      </el-row>
    </card-chart>
  </el-row>
</template>

<script>
import cardChart from "../../graphs/cardChart";
import { feedConfig, ABWConfig, doConfig } from "../utils/chartOptions";
import { mapGetters, mapActions } from "vuex";
import errorHandlerMixin from "@/mixins/errorHandlerMixin";
import { getColorWithIndex } from "@/config/baseChartConfig";
export default {
  name: "InfoChart",
  mixins: [errorHandlerMixin],
  data: function() {
    return {
      feedChartOptions: feedConfig,
      abwChartOptions: ABWConfig,
      doChartOptions: doConfig,
      doQuery: {},
      feedXAxisType: "datetime",
      loading: {
        FEED: false,
        DO: false,
        ABW: false,
      },
      colorToPondMap: {},
      stackOfcolorNotAllocated: [],
    };
  },
  components: {
    cardChart,
  },
  computed: {
    ...mapGetters("pondInfo", {
      selectedPonds: "selectedPonds",
      doData: "doData",
      scheduleFeed: "scheduleFeed",
      abwData: "abwData",
      dateRangeForFeedGraph: "getDateRangeForFeedGraph",
      dateRangeForABWGraph: "getDateRangeForABWGraph",
      dateRangeForDOGraph: "getDateRangeForDOGraph",
      getAllGraphLoading: "getAllGraphLoading",
      getMinStartCultureDate: "getMinStartCultureDate",
      getPondColorIds: "getPondColorIds",
    }),
    ...mapGetters("user", {
      getUserTimeZoneOffset: "getUserTimeZoneOffset",
    }),
    getDatePickerOptions: function() {
      const soc = this.getMinStartCultureDate;
      const userTimeZoneOffset = this.getUserTimeZoneOffset;
      const diffBtnCurrToSocInDays = Math.abs(
        this.$moment(soc)
          .utcOffset(userTimeZoneOffset)
          .diff(this.$moment().utcOffset(userTimeZoneOffset), "days")
      );
      const getshortCutObj = (no, unit, text) => {
        return {
          no,
          unit,
          text,
        };
      };
      const getFormat = (momentObj) => {
        return momentObj.format("YYYY-MM-DD");
      };
      return {
        disabledDate: (time) => {
          return !this.$moment(time).isBetween(
            this.$moment
              .utc(soc)
              .utcOffset(userTimeZoneOffset)
              .subtract(1, "days"),
            this.$moment().utcOffset(userTimeZoneOffset),
            "hours"
          );
        },
        shortcuts: [
          getshortCutObj(
            diffBtnCurrToSocInDays + 1,
            "days",
            `${this.$t("Comn_from")} ${this.$t("Comn_soc")}`
          ),
          getshortCutObj(1, "days", this.$t("Comn_yesterday")),
          getshortCutObj(6, "days", this.$t("last_7days")),
          getshortCutObj(14, "days", this.$t("last_15days")),
          getshortCutObj(1, "months", this.$t("last_30days")),
          getshortCutObj(3, "months", this.$t("last_3months")),
          getshortCutObj(6, "months", this.$t("last_6months")),
        ].map((shortCut) => {
          return {
            text: shortCut.text,
            onClick: (picker) => {
              const end = this.$moment().utcOffset(userTimeZoneOffset);
              const start = this.$moment().utcOffset(userTimeZoneOffset);
              start.subtract(shortCut.no, shortCut.unit);
              picker.$emit("pick", [getFormat(start), getFormat(end)]);
            },
          };
        }),
      };
    },
    getHourlyDatePickerOptions: function() {
      const soc = this.getMinStartCultureDate;
      const userTimeZoneOffset = this.getUserTimeZoneOffset;
      const diffBtnCurrToSocInDays = Math.abs(
        this.$moment(soc)
          .utcOffset(userTimeZoneOffset)
          .diff(this.$moment().utcOffset(userTimeZoneOffset), "days")
      );
      const getshortCutObj = (no, unit, customText = undefined) => {
        return {
          no,
          unit,
          text:
            customText || `${this.$t("Comn_last")} ${no} ${this.$tc(unit, 2)}`,
        };
      };
      const getFormat = (momentObj) => {
        return momentObj.format("YYYY-MM-DD");
      };
      return {
        disabledDate: (time) => {
          return !this.$moment(time).isBetween(
            this.$moment
              .utc(soc)
              .utcOffset(userTimeZoneOffset)
              .add(1, "days"),
            this.$moment().utcOffset(userTimeZoneOffset),
            "days"
          );
        },
        shortcuts: [
          getshortCutObj(
            diffBtnCurrToSocInDays + 1,
            "days",
            `${this.$t("Comn_from")} ${this.$t("Comn_soc")}`
          ),
          getshortCutObj(6, "hours"),
          getshortCutObj(2, "days"),
          getshortCutObj(3, "days"),
          getshortCutObj(5, "days"),
          getshortCutObj(1, "months"),
          getshortCutObj(3, "months"),
          getshortCutObj(6, "months"),
        ].map((shortCut) => {
          return {
            text: shortCut.text,
            onClick: (picker) => {
              const end = this.$moment().utcOffset(userTimeZoneOffset);
              const start = this.$moment().utcOffset(userTimeZoneOffset);
              start.subtract(shortCut.no - 1, shortCut.unit);
              picker.$emit("pick", [getFormat(start), getFormat(end)]);
            },
          };
        }),
      };
    },
    getLegend() {
      const list = [];
      const series = this.getScheduleFeedChartOptions.series;
      for (var i = 0; i < series.length - 1; i++) {
        const { name, colorIndex } = series[i];
        if (i === 0 || !(i % 2)) {
          list.push({
            name,
            color: getColorWithIndex(colorIndex),
          });
        }
      }
      return list;
    },
    getSelectedPonds() {
      return this.selectedPonds;
    },
    getDOData() {
      return this.doData;
    },
    getScheduleFeedData() {
      return this.scheduleFeed;
    },
    getABWData() {
      return this.abwData;
    },
    getPondDarkColorId() {
      return (pondId) => {
        return 2 * this.getPondColorIds[pondId];
      };
    },
    getPondLightColorId() {
      return (pondId) => {
        return 2 * this.getPondColorIds[pondId] + 1;
      };
    },
    getFeedTableData() {
      const data = (this.getScheduleFeedData || []).map(
        ({ title, feedList, dispFeedList }) => {
          const arr3 = feedList.map((item, i) =>
            Object.assign({}, item, dispFeedList[i])
          );
          var result = {};
          for (var i = 0; i < arr3.length; i++) {
            const item = {
              dispensedFeed: arr3[i].dispensed_feed,
              feed: arr3[i].feed,
            };
            result[arr3[i].newDate] = item;
          }
          result.title = title;
          return result;
        }
      );
      return data;
    },
    getAbwTableData() {
      const data = this.getABWData.map(({ title, abwData, biomassData }) => {
        const arr3 = abwData.map((item, i) =>
          Object.assign({}, item, biomassData[i])
        );
        var result = {};
        for (var i = 0; i < arr3.length; i++) {
          const item = {
            abwData: arr3[i].abw,
            biomassData: arr3[i].biomass,
          };
          result[arr3[i].formattedDate] = item;
        }
        result.title = title;
        return result;
      });
      return data;
    },
    getDoTableData() {
      const data = this.getDOData.reduce(
        (tableDataArr, { title, doData, feedData }) => {
          const rowData = [
            { data: doData, key: "doData", value_key: "dissolved_oxygen" },
            { data: feedData, key: "feedData", value_key: "dispensed_feed" },
          ].reduce((rowDataObj, dataIterObj) => {
            if (dataIterObj.data.length > 0) {
              dataIterObj.data.forEach((dataItem) => {
                if (!rowDataObj[dataItem.newDate]) {
                  rowDataObj[dataItem.newDate] = {};
                }
                rowDataObj[dataItem.newDate][dataIterObj.key] =
                  dataItem[dataIterObj.value_key];
              });
            }
            return rowDataObj;
          }, {});
          if (Object.keys(rowData).length > 0) {
            rowData.title = title;
            tableDataArr.push(rowData);
          }
          return tableDataArr;
        },
        []
      );
      return data;
    },
    getScheduleFeedChartOptions() {
      const series = [];
      const ponds = this.selectedPonds.reduce((acc, pond) => {
        if (pond) {
          acc[pond._id] = pond;
        }
        return acc;
      }, {});
      const pondCount = this.selectedPonds.length;
      const xAxisGenerator =
        pondCount === 1
          ? (date) => date
          : (date, pond_id) => {
              return this.$moment(date).diff(
                this.$moment(
                  (ponds[pond_id] || { cultivation_date: new Date() })
                    .cultivation_date
                ),
                "days"
              );
            };
      let xMin = Date.now();
      let xMax = 0;
      for (var i = 0; i < this.getScheduleFeedData.length; i++) {
        const {
          dispFeedList,
          feedList,
          title,
          pond_id,
        } = this.getScheduleFeedData[i];

        const fFeed = feedList.reduce((acc, { newDate, feed }, index) => {
          const x = xAxisGenerator(newDate, pond_id);
          if (x > 0) {
            if (i === 0) {
              xMin = x;
              xMax = x;
            }
            xMax = Math.max(x, xMax);
            xMin = Math.min(x, xMin);
            acc.push({
              x: xAxisGenerator(newDate, pond_id),
              y: feed || 0,
            });
          }
          return acc;
        }, []);
        const fdFeed = dispFeedList.reduce(
          (acc, { newDate, dispensed_feed }, index) => {
            const x = xAxisGenerator(newDate, pond_id);
            if (x > 0) {
              xMax = Math.max(x, xMax);
              xMin = Math.min(x, xMin);
              acc.push({
                x: xAxisGenerator(newDate, pond_id),
                y: Number(dispensed_feed),
                feed_value: (feedList[index] || { feed: 0 }).feed,
              });
            }
            return acc;
          },
          []
        );
        series.push({
          name: title,
          nameLg: this.$t("PM_dispensed_feed_kg"),
          tableLgOrder: 1,
          visible: true,
          data: fdFeed,
          linkedTo: title,
          dashStyle: "Solid",
          colorIndex: this.getPondDarkColorId(pond_id),
          zIndex: 0,
          tooltip: {
            footerFormat: "</table>",
            headerFormat: `<small><strong>${
              pondCount > 1 ? "DOC: " : ""
            }{point.key}</strong></small><table>`,
            pointFormatter: function() {
              return (
                `<tr><td class="highcharts-tooltip__point__title" style="color: ${this.series.color}">${this.series.name}: </td>` +
                `<td style="text-align: right"><b>${this.y
                  .toFixed(2)
                  .toString()}</b>/<b>${+this.feed_value
                  .toFixed(2)
                  .toString()}</b></td></tr>`
              );
            },
          },
          marker: {
            enabled: false,
          },
        });

        series.push({
          name: title,
          nameLg: this.$t("PM_day_feed") + " (kg)",
          tableLgOrder: 2,
          data: fFeed,
          id: title,
          colorIndex: this.getPondLightColorId(pond_id),
          zIndex: 1,
          dashStyle: "dash",
          tooltip: {
            pointFormat: "",
          },
          visible: true,
        });
      }
      if (pondCount > 1) {
        feedConfig.chart_options.xAxis.type = "linear";
        feedConfig.chart_options.xAxis.allowDecimals = true;
        feedConfig.chart_options.xAxis.min = xMin;
        feedConfig.chart_options.xAxis.max = xMax;
        feedConfig.chart_options.xAxis.minRange = 10;
        feedConfig.chart_options.xAxis.title.text = this.$t("Comn_doc_days");
      } else {
        feedConfig.chart_options.xAxis.type = "datetime";
        feedConfig.chart_options.xAxis.allowDecimals = false;
        feedConfig.chart_options.xAxis.min = xMin;
        feedConfig.chart_options.xAxis.max = xMax;
        feedConfig.chart_options.xAxis.minRange =
          series.length > 0 ? 24 * 1000 * 60 * 60 * 2 : undefined;
        feedConfig.chart_options.xAxis.title.text = this.$t("Comn_date");
      }
      return {
        ...feedConfig.chart_options,
        series,
      };
    },
    getABWChartOptions() {
      const series = [];
      let xMax = 0;
      let xMin = Date.now();
      for (var i = 0; i < this.getABWData.length; i++) {
        const { abwData, biomassData, title, pondId } = this.getABWData[i];
        const fAwb = abwData.map(({ formattedDate, abw }) => {
          xMax = Math.max(formattedDate, xMax);
          xMin = Math.min(formattedDate, xMin);
          return [formattedDate, +abw];
        });
        const fBio = biomassData.map(({ formattedDate, biomass }, index) => {
          xMax = Math.max(formattedDate, xMax);
          xMin = Math.min(formattedDate, xMin);
          return {
            x: formattedDate,
            y: +biomass,
            abw: +(abwData[index] || { abw: 0 }).abw,
          };
        });
        series.push({
          name: title,
          nameLg: this.$t("PM_bio_mass_kg"),
          tableLgOrder: 2,
          type: "column",
          data: fBio,
          maxPointWidth: 20,
          zIndex: 1,
          yAxis: 1,
          colorIndex: this.getPondDarkColorId(pondId),
          dashStyle: "dash",
          visible: true,
          tooltip: {
            footerFormat: "</table>",
            headerFormat: "<small><strong>{point.key}</strong></small><table>",
            pointFormatter: function() {
              return (
                `<tr><td class="highcharts-tooltip__point__title" style="color: ${this.series.color}">${this.series.name}: </td>` +
                `<td style="text-align: right"><b>${(+this.abw.toFixed(
                  2
                )).toString()}</b>/<b>${(+this.y.toFixed(
                  2
                )).toString()}</b></td></tr>`
              );
            },
          },
          stack: 0,
        });
        series.push({
          name: title,
          nameLg: this.$t("abw_gms"),
          tableLgOrder: 1,
          type: "spline",
          colorIndex: this.getPondDarkColorId(pondId),
          data: fAwb,
          dashStyle: "dash",
          zIndex: 2,
          yAxis: 0,
          tooltip: {
            pointFormat: "",
          },
          visible: true,
          stack: 0,
        });
      }
      ABWConfig.chart_options.xAxis.min = xMin;
      ABWConfig.chart_options.xAxis.max = xMax;
      return {
        ...ABWConfig.chart_options,
        series,
      };
    },
    getDOChartOptions() {
      const series = [];
      let xMin = Date.now();
      let xMax = 0;
      for (var i = 0; i < this.getDOData.length; i++) {
        const { doData, feedData, title, pondId } = this.getDOData[i];
        const dateToFeedMap = {};
        const fFeed = feedData.map(({ newDate, dispensed_feed }, index) => {
          xMax = Math.max(newDate, xMax);
          xMin = Math.min(newDate, xMin);
          dateToFeedMap[newDate] = +dispensed_feed;
          return {
            x: newDate,
            y: +dispensed_feed,
          };
        });
        const fDo = doData.map(({ newDate, dissolved_oxygen }) => {
          xMax = Math.max(newDate, xMax);
          xMin = Math.min(newDate, xMin);
          return {
            x: newDate,
            y: +dissolved_oxygen,
            feed: +dateToFeedMap[newDate] || 0,
          };
        });
        const valueFormatter = (value) => {
          return +value.toFixed(2).toString();
        };

        series.push({
          name: title,
          nameLg: this.$t("Comn_hourly_feed") + " (kg)",
          tableLgOrder: 2,
          type: "column",
          data: fFeed,
          zIndex: 1,
          yAxis: 0,
          colorIndex: this.getPondDarkColorId(pondId),
          visible: true,
          tooltip: {
            xDateFormat: "%Y-%m-%d %H:%M",
            pointFormatter: function() {
              return (
                `<tr><td class="highcharts-tooltip__point__title" style="color: ${this.series.color}">${this.series.name}: </td>` +
                `<td style="text-align: right"><b>${valueFormatter(
                  this.y
                )}</b></td></tr>`
              );
            },
          },
        });
        series.push({
          name: title,
          nameLg: this.$t("Comn_do_with_units"),
          tableLgOrder: 1,
          type: "spline",
          data: fDo,
          zIndex: 1,
          dashStyle: "dash",
          tooltip: {
            xDateFormat: "%Y-%m-%d %H:%M",
            headerFormat: "<small><strong>{point.key}</strong></small><table>",
            pointFormatter: function() {
              return (
                `<tr><td class="highcharts-tooltip__point__title" style="color: ${this.series.color}">${this.series.name}: </td>` +
                `<td style="text-align: right"><b>${valueFormatter(
                  this.y
                )}</b>/<b>${valueFormatter(this.feed)}</b></td></tr>`
              );
            },
            footerFormat: "</table>",
          },
          yAxis: 1,
          colorIndex: this.getPondDarkColorId(pondId),
          visible: true,
          // stack: 0
        });
        doConfig.chart_options.xAxis.min = xMin;
        doConfig.chart_options.xAxis.max = xMax;
        doConfig.chart_options.tooltip.shared = false;
      }
      return {
        ...doConfig.chart_options,
        series,
      };
    },
    getScheduleFeedColumns() {
      this.$moment.locale(this.$i18n.locale);
      if (this.getFeedTableData.length > 0) {
        const columns = Object.keys(this.getFeedTableData[0]);
        columns.pop();
        columns.sort((a, b) => b - a);
        const newColumns = {
          title: {
            label: this.$t("Comn_pond_name"),
            width: "100",
            fixed: true,
          },
        };
        for (var i = 0; i < columns.length; i++) {
          const date = new Date(parseInt(columns[i]));
          newColumns[columns[i]] = {
            label: this.$moment(date).format("D MMM"),
            // width: "60"
          };
        }
        return newColumns;
      } else {
        return undefined;
      }
    },
    getAbwColumns() {
      this.$moment.locale(this.$i18n.locale);
      if (this.getAbwTableData.length > 0) {
        const columns = Object.keys(this.getAbwTableData[0]);
        if (columns.length === 1) {
          return undefined;
        }
        columns.pop();
        columns.sort((a, b) => b - a);
        const newColumns = {
          title: {
            label: this.$t("Comn_pond_name"),
            width: "100",
            fixed: true,
            // prop: "title"
          },
        };
        for (var i = 0; i < columns.length; i++) {
          const date = new Date(parseInt(columns[i]));
          newColumns[columns[i]] = {
            label: this.$moment(date).format("D MMM"),
            // width: "60"
          };
        }
        return newColumns;
      } else {
        return undefined;
      }
    },
    getDoColumns() {
      this.$moment.locale(this.$i18n.locale);
      if (this.getDoTableData.length > 0) {
        const columns = Object.keys(this.getDoTableData[0]);
        columns.pop();
        columns.sort((a, b) => b - a);
        const newColumns = {
          title: {
            label: this.$t("Comn_pond_name"),
            width: "100",
            fixed: true,
          },
        };
        for (var i = 0; i < columns.length; i++) {
          const date = new Date(parseInt(columns[i]));
          newColumns[columns[i]] = {
            label: this.$moment(date).format("D MMM"),
            subLabel: this.$moment(date).format("H:mm"),
            // width: "60"
          };
        }
        return newColumns;
      } else {
        return undefined;
      }
    },
    getDateRangeForFeedGraph() {
      return this.dateRangeForFeedGraph;
    },
    getDateRangeForABWGraph() {
      return this.dateRangeForABWGraph;
    },
    getDateRangeForDOGraph() {
      return this.dateRangeForDOGraph;
    },
  },
  methods: {
    ...mapActions("pondInfo", {
      changeDateRangeInGraph: "changeDateRangeInGraph",
      changeAllGraphLoadingStatus: "changeAllGraphLoadingStatus",
    }),
    async handleDateQuery(dateKey, graphType) {
      this.loading[graphType] = true;
      try {
        const data = {
          graph_key: graphType,
          date: dateKey,
        };
        await this.changeDateRangeInGraph(data);
      } catch (err) {
        this.errorMessages(err, true);
      } finally {
        this.loading[graphType] = false;
        this.changeAllGraphLoadingStatus(false);
      }
    },
  },
};
</script>

<style lang="scss">
@import "../../../../styles/mixins.scss";
.info-charts {
  & > div:not(:last-child) {
    margin-bottom: 10px;
  }

  .legend-5-p {
    flex-direction: column !important;
    align-content: center;
    align-items: center;
  }
  .legend-container {
    display: flex;
    justify-content: flex-start;
    padding-right: 10px;
    padding-left: 1.5em;
    padding-top: 5px;
    padding-bottom: 5px;
    max-width: 100%;
    overflow: hidden;
    flex-wrap: wrap;
  }
  .table-row .legend-container {
    padding-top: 15px;
  }
  .legend {
    display: flex;
    justify-content: flex-start;
    &--right {
      justify-content: flex-end;
    }
    .legend__item {
      display: inline-flex;
      align-items: center;
      &:not(:first-child) {
        margin-left: 10px;
      }
      .title {
        @include responsiveProperty(
          font-size,
          $app_font_size_small,
          $app_font_size_1,
          $app_font_size_2
        );
        margin-left: 3px;
      }
      &--dash-line {
        hr {
          width: 1em;
          min-height: 0;
          border-style: unset;
          border-top: 2px dashed black;
        }
      }
      &--solid-line {
        hr {
          width: 1em;
          min-height: 0;
          background-color: black;
          border-top: 1px solid black;
        }
      }
    }
    .color-box {
      @include responsiveProperty(
        width,
        $app_font_size_small,
        $app_font_size_1,
        $app_font_size_2
      );
      @include responsiveProperty(
        height,
        $app_font_size_small,
        $app_font_size_1,
        $app_font_size_2
      );
      // width: 11px;
      // height: 11px;
      border-radius: 4px;
    }
  }
}
</style>
